import React, { useState, useEffect, useMemo } from "react";
import { Upload, message } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import type { UploadChangeParam, UploadFile } from "antd/es/upload";
import type {
  RcFile,
  UploadFileStatus,
  UploadProps,
} from "antd/es/upload/interface";
import { httpClient } from "@app/api/base";
import { OSSUploadResponse } from "@app/api/admin";

interface OSSImageUploadProps {
  value?: string[];
  onChange?: (urls: string[]) => void;
  maxCount?: number;
  disabled?: boolean;
}

interface CustomUploadFile extends UploadFile {
  url?: string;
  status: UploadFileStatus;
}

// 定义 API 响应类型
interface OSSUpload {
  data: {
    success: boolean;
    message?: string;
    data: {
      download_url: string;
    };
  };
}

const OSSImageUpload: React.FC<OSSImageUploadProps> = ({
  value = [],
  onChange,
  maxCount = 1,
  disabled = false,
  ...props
}) => {
  const [fileList, setFileList] = useState<CustomUploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isInternalUpdate, setIsInternalUpdate] = useState(false);

  // 初始化文件列表 - 修复图片回显问题
  useEffect(() => {
    // 如果是内部更新触发的，跳过这次 effect
    if (isInternalUpdate) {
      setIsInternalUpdate(false);
      return;
    }

    const initialFileList: CustomUploadFile[] = value.map((url, index) => ({
      uid: `initial-${index}-${Date.now()}`,
      name: `image-${index}.png`,
      status: "done" as const,
      url: url,
      thumbUrl: url,
    }));

    setFileList((prevList) => {
      const prevUrls = prevList.map((file) => file.url).filter(Boolean);
      const newUrls = value;

      // 只有当 URL 数组真正不同时才更新
      if (JSON.stringify(prevUrls.sort()) !== JSON.stringify(newUrls.sort())) {
        return initialFileList;
      }
      return prevList;
    });
  }, [value, isInternalUpdate]);

  // 文件上传前的验证
  const beforeUpload = (file: RcFile): boolean => {
    const isValidType = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "image/webp",
    ].includes(file.type);
    if (!isValidType) {
      message.error("只支持上传 JPG、PNG、WEBP 格式的图片！");
      return false;
    }

    const isValidSize = file.size / 1024 / 1024 < 20;
    if (!isValidSize) {
      message.error("图片大小不能超过 20MB！");
      return false;
    }

    return true;
  };

  // 自定义上传请求
  const customRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file as RcFile);

      const response = await httpClient.post<OSSUpload>(
        "/oss/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.success && response.data?.data?.download_url) {
        // ?response-content-disposition=attachment 返回的图片地址截取掉这段
        const uploadedUrl = response.data.data.download_url.replace(
          "?response-content-disposition=attachment",
          ""
        );

        // 设置内部更新标志
        setIsInternalUpdate(true);
        
        onSuccess(
          {
            url: uploadedUrl,
            status: "done",
          },
          file
        );

        message.success("图片上传成功！");
      } else {
        throw new Error(response.data?.message || "上传失败");
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      onError(error);
      message.error(`上传失败: ${error.message || "未知错误"}`);
    } finally {
      setUploading(false);
    }
  };

  // 处理文件列表变化
  const handleChange: UploadProps["onChange"] = (
    info: UploadChangeParam<UploadFile>
  ) => {
    let newFileList = [...info.fileList] as CustomUploadFile[];

    // 处理上传成功的文件
    newFileList = newFileList.map((file) => {
      if (file.status === "done" && file.response?.url) {
        return {
          ...file,
          url: file.response.url,
          thumbUrl: file.response.url,
        };
      }
      return file;
    });

    // 过滤掉上传失败的文件
    newFileList = newFileList.filter((file) => file.status !== "error");

    setFileList(newFileList);

    // 提取有效的 URL 并触发 onChange
    const validUrls = newFileList
      .filter((file) => file.status === "done" && file.url)
      .map((file) => file.url!);

    // 设置内部更新标志，防止 useEffect 覆盖
    setIsInternalUpdate(true);
    onChange?.(validUrls);
  };

  // 处理文件移除
  const handleRemove = (file: UploadFile): boolean => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);

    const validUrls = newFileList
      .filter((item) => item.status === "done" && item.url)
      .map((item) => item.url!);

    // 设置内部更新标志
    setIsInternalUpdate(true);
    onChange?.(validUrls);
    return true;
  };

  // 上传按钮
  const uploadButton = (
    <div>
      {uploading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>上传图片</div>
    </div>
  );

  return (
    <Upload
      name="file"
      listType="picture-card"
      className="oss-image-uploader"
      fileList={fileList}
      beforeUpload={beforeUpload}
      customRequest={customRequest}
      onChange={handleChange}
      onRemove={handleRemove}
      multiple={maxCount > 1}
      maxCount={maxCount}
      disabled={disabled || uploading}
      showUploadList={{
        showPreviewIcon: true,
        showRemoveIcon: !disabled,
        showDownloadIcon: false,
      }}
      {...props}
    >
      {fileList.length >= maxCount ? null : uploadButton}
    </Upload>
  );
};

export default OSSImageUpload;
